import * as vp from 'vopay';
import {
  Box,
  Card,
  Title,
  Pagination,
  Table,
  Icon,
  usePagination,
  useUpdateQueryParams,
  capitalizeFirstLetter,
  GetColorStatus,
} from 'vopay';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import WebhooksHistorySearchForm from './WebhooksHistorySearchForm';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import useFetchWebhooks from '@/hooks/useApi/useFetchWebhooks';
import usePostResendWebhook from '@/hooks/useApi/usePostResendWebhook';
import styles from './WebhooksHistory.module.scss';
import { useToast } from '@/providers/Toast.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { format, parseISO } from 'date-fns';
import { Button, TableFiltersContainer, UtilButton, TransactionStatus } from 'UI';
import { useHistory } from 'react-router-dom';

type QueryParams = {
  eventType: string;
  startDate: string;
  endDate: string;
};

type WebhooksSearchParams = {
  eventType?: string;
  timeRange?: [vp.Timestamp, vp.Timestamp];
  timeZone?: string;
};

type FC = FunctionComponent;

type FilteredListType = vp.WebhooksList;

const WebhooksHistoryContainer: FC = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'WebhooksHistory' });
  const { t: tWebhooks } = useTranslation('translations', { keyPrefix: 'Settings.Webhooks' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { userSettings } = useUserSettingsProvider();
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [webhooksResponse, , , getWebhooks] = useFetchWebhooks({});
  // bulk trigger webhooks feature (yet to implement)
  // const [allCheckbox, setAllCheckbox] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);
  const [, , , postResendWebhook] = usePostResendWebhook({});
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const history = useHistory();
  const timeDifference = moment().subtract(1, 'month').format('YYYY-MM-DD');
  const currentDateTime = moment().format('YYYY-MM-DD');
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const startDate = useMemo(() => queryParams.get('startDate') || '', [queryParams]);
  const endDate = useMemo(() => queryParams.get('endDate') || '', [queryParams]);
  const eventType = useMemo(() => queryParams.get('eventType') || '', [queryParams]);
  const filtersApplied = useMemo(() => {
    return Boolean(
      (startDate && startDate !== timeDifference) || (endDate && endDate !== currentDateTime) || eventType,
    );
  }, [startDate, endDate, eventType, timeDifference, currentDateTime]);

  const [tempFilteredLists, setTempFilteredLists] = useState<FilteredListType[]>([]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  // pagination
  const totalDataLength = useMemo(() => {
    return webhooksResponse.Pagination?.TotalRecords || 0;
  }, [webhooksResponse.WebhookNotifications]);
  const PER_PAGE = 20;
  const { pageCount, pageIndex, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const search = async (offset = 0, searchParams: WebhooksSearchParams = {}) => {
    const defaultValues = {
      StartDate: format(searchParams.timeRange?.[0] ?? parseISO(startDate || timeDifference), 'yyyy-MM-dd'),
      EndDate: format(searchParams.timeRange?.[1] ?? parseISO(endDate || currentDateTime), 'yyyy-MM-dd'),
      ...(searchParams.eventType === 'all' ? {} : { Type: searchParams.eventType }),
      Timezone: userSettings.timezone,
      Limit: PER_PAGE,
      Offset: offset,
    };

    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);
      return;
    }
    try {
      const res = await getWebhooks(defaultValues);

      if (!res.Success) throw new Error(res.ErrorMessage);
      const webhooksRequest = Object.values(res.WebhookNotifications || {});
      setTempFilteredLists(webhooksRequest);
      setInitialLoad(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('ErrorMessages.fetchWebhooks');
      alertDialog.error(t('ErrorMessages.error'), msg);
      console.error(err);
    }
  };

  useEffect(() => {
    if (startDate > endDate) {
      setHasInvalidEndDate(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [endDate]);

  useEffect(() => {
    const offset = setPageIndex(0);

    const searchParams = {
      eventType,
      startDate: format(parseISO(startDate || timeDifference), 'yyyy-MM-dd'),
      endDate: format(parseISO(endDate || currentDateTime), 'yyyy-MM-dd'),
    };
    updateQueryParams(searchParams);
    search(offset, searchParams);
  }, []);

  async function retriggerWebhooks(webhook: vp.RetriggerWebhookRequest) {
    const params: vp.RetriggerWebhookRequest = {
      EventID: webhook.EventID,
      Type: webhook.Type,
    };
    try {
      const res = await postResendWebhook(params);
      if (res.Success) {
        toast.success(<strong>{t('ToastMessage.retriggerSuccess')}</strong>);
      } else {
        toast.neutral(<strong>{t('ToastMessage.retriggerFail')}</strong>);
      }
    } catch {
      toast.neutral(<strong>{t('ToastMessage.retriggerFail')}</strong>);
    }
  }

  const tableProps: vp.TableProps<vp.WebhooksList> = {
    id: 'webhooksHistoryTable',
    version: '2',
    zebra: true,
    columns: [
      { id: 'dateSent', header: t('Columns.dateSent'), align: 'left', field: 'DateSent' },
      { id: 'event', header: t('Columns.event'), align: 'left', field: 'Type' },
      { id: 'transactionType', header: t('Columns.transactionType'), align: 'left', field: 'Module' },
      { id: 'payloadId', header: t('Columns.payloadId'), align: 'left', field: 'WebhookID' },
      {
        id: 'notificationStatus',
        header: t('Columns.notificationStatus'),
        align: 'left',
        template: ({ data }) => (
          <TransactionStatus
            status={GetColorStatus(data.NotificationStatus || '')}
            label={capitalizeFirstLetter(data.NotificationStatus)}
          />
        ),
      },
      { id: 'responseStatusCode', header: t('Columns.responseStatusCode'), align: 'left', field: 'ResponseStatusCode' },
      { id: 'responseText', header: t('Columns.responseText'), align: 'left', field: 'ResponseText' },
      {
        id: 'payloadStatus',
        header: t('Columns.payloadStatus'),
        align: 'left',
        template: ({ data }) => (
          <TransactionStatus status={GetColorStatus(data.Status || '')} label={capitalizeFirstLetter(data.Status)} />
        ),
      },
      {
        id: 'retrigger',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box jc="fe">
            <Button
              key={`retrigger-webhook-${data.WebhookID}`}
              id={`webhook-${data.WebhookID}`}
              size="small"
              onClick={() => {
                retriggerWebhooks(data);
              }}
            >
              {t('Columns.retrigger')}
            </Button>
          </Box>
        ),
      },
    ],
    rows: tempFilteredLists.map((item): { id: string; data: vp.WebhooksList } => ({
      id: String(item.WebhookID || ''),
      data: item,
    })),
  };

  const EmptyOrNoResults = () => {
    const isEmpty = initialLoad && tableProps.rows?.length === 0;
    const isNoSearch = !initialLoad;

    const message = isNoSearch ? t('noSearchMessage') : t('noRecordMessage');

    return isEmpty || isNoSearch ? (
      <Box flx="col" className={styles.webhooksTableContainer} width="100%" gap="common">
        <Box flx="col" jc="center" gap="sm">
          {isNoSearch && (
            <Box ai="center" jc="center" height="auto">
              <Icon icon="search2" size="lg" />
            </Box>
          )}
          <Box ai="center" jc="center" pt="md" height="auto">
            {message}
          </Box>
        </Box>
      </Box>
    ) : null;
  };

  return (
    <>
      <Box flx="col" gapSize="36px">
        <Box flx="col" gapSize="16px">
          <UtilButton
            iconLeft="chevron_left"
            size="large"
            onClick={() => {
              if (history.length > 1) {
                history.goBack();
              } else {
                history.replace('/settings/webhooks');
              }
            }}
          >
            {history.length > 1 ? tGlobal('Buttons.back') : tWebhooks('pageTitle')}
          </UtilButton>
          <Title title={t('title')} />
        </Box>
        <Card
          style={{
            padding: '16px',
          }}
        >
          <Box flx="col" gap="sm">
            <TableFiltersContainer filtersApplied={filtersApplied}>
              <TableFiltersContainer.Trigger />
              <TableFiltersContainer.Content>
                <WebhooksHistorySearchForm
                  eventType={eventType}
                  initialTimeRange={[
                    parseISO(startDate || timeDifference).getTime() as vp.Timestamp,
                    parseISO(endDate || currentDateTime).getTime() as vp.Timestamp,
                  ]}
                  hasInvalidEndDate={hasInvalidEndDate}
                  shouldShowEndDateError={shouldShowEndDateError}
                  setShouldShowEndDateError={setShouldShowEndDateError}
                  search={(searchParams) => {
                    const offset = setPageIndex(0);
                    updateQueryParams({
                      eventType: searchParams.eventType || '',
                      startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                      endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                    });
                    search(offset, searchParams);
                  }}
                />
              </TableFiltersContainer.Content>
            </TableFiltersContainer>
          </Box>
          <Box>
            <Table {...tableProps} />
          </Box>
          <EmptyOrNoResults />
          <Box width="100%" jc="center" mt="lg" mb="sm">
            {pageCount > 1 && (
              <Pagination
                pageCount={pageCount}
                forcePageNumber={pageIndex}
                handlePageClick={(e) => {
                  const offset = setPageIndex(e.selected);
                  const searchParams = {
                    eventType,
                    startDate: format(new Date(startDate || timeDifference), 'yyyy-MM-dd'),
                    endDate: format(new Date(endDate || currentDateTime), 'yyyy-MM-dd'),
                  };
                  search(offset, searchParams);
                }}
              />
            )}
          </Box>
        </Card>
      </Box>
    </>
  );
};

export default WebhooksHistoryContainer;
